import { useEffect, useLayoutEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import {createPortal} from 'react-dom';

export type ConfirmationDialogTone='warning'|'danger';

export type ConfirmationDialogProps=Readonly<{
  open:boolean;
  title:string;
  description:ReactNode;
  confirmLabel:string;
  busyLabel:string;
  cancelLabel?:string;
  tone:ConfirmationDialogTone;
  busy?:boolean;
  onConfirm:()=>void|Promise<void>;
  onCancel:()=>void;
  restoreFocusRef?:RefObject<HTMLElement|null>;
  /** Keeps the confirm button disabled, for example until a required reason is long enough. */
  confirmDisabled?:boolean;
  children?:ReactNode;
}>;

export function ConfirmationDialog({
  open,
  title,
  description,
  confirmLabel,
  busyLabel,
  cancelLabel='Cancel',
  tone,
  busy=false,
  onConfirm,
  onCancel,
  restoreFocusRef,
  confirmDisabled=false,
  children,
}:ConfirmationDialogProps){
  const dialogRef=useRef<HTMLDialogElement>(null);
  const cancelRef=useRef<HTMLButtonElement>(null);
  const openerRef=useRef<HTMLElement|null>(null);
  const wasOpenRef=useRef(false);
  const confirmInFlightRef=useRef(false);
  const mountedRef=useRef(true);
  const[confirming,setConfirming]=useState(false);
  const[fullPage,setFullPage]=useState(false);
  const pageRestoreRef=useRef<(()=>void)|null>(null);
  const scrollRef=useRef(0);
  const titleId=useId();
  const descriptionId=useId();
  const protectedBusy=busy||confirming;

  useEffect(()=>{mountedRef.current=true;return()=>{mountedRef.current=false}},[]);

  useLayoutEffect(()=>{
    const dialog=dialogRef.current;
    if(!dialog)return;

    if(open){
      if(!wasOpenRef.current){
        openerRef.current=document.activeElement instanceof HTMLElement?document.activeElement:null;
        scrollRef.current=window.scrollY;
        wasOpenRef.current=true;
      }
      if(!dialog.open)dialog.showModal();
      function expandIfNeeded(){
        if(pageRestoreRef.current||!dialog)return;
        const surface=dialog.querySelector<HTMLElement>('.confirmation-dialog__surface');
        if(!surface||surface.getBoundingClientRect().height<=window.innerHeight-32)return;
        // A long confirmation is a dedicated page. Keep the underlying mounted
        // form intact so cancelling restores its unsaved fields and focus.
        const root=document.getElementById('root'),wasHidden=root?.hidden;
        dialog.close();dialog.dataset.page='true';
        if(root)root.hidden=true;
        pageRestoreRef.current=()=>{if(root)root.hidden=wasHidden??false;};
        setFullPage(true);dialog.show();
        dialog.querySelector<HTMLElement>('h2')?.focus({preventScroll:true});
        window.scrollTo({top:0});
      }
      expandIfNeeded();
      requestAnimationFrame(()=>{if(!pageRestoreRef.current)cancelRef.current?.focus();});
      const observer=new ResizeObserver(expandIfNeeded);
      const surface=dialog.querySelector('.confirmation-dialog__surface');
      if(surface)observer.observe(surface);
      window.addEventListener('resize',expandIfNeeded);
      return()=>{observer.disconnect();window.removeEventListener('resize',expandIfNeeded);};
    }

    if(dialog.open)dialog.close();
    pageRestoreRef.current?.();pageRestoreRef.current=null;
    delete dialog.dataset.page;setFullPage(false);
    if(wasOpenRef.current){
      wasOpenRef.current=false;
      confirmInFlightRef.current=false;
      setConfirming(false);
      const target=openerRef.current?.isConnected?openerRef.current:restoreFocusRef?.current;
      requestAnimationFrame(()=>{window.scrollTo({top:scrollRef.current});target?.focus({preventScroll:true});});
    }
  },[open,restoreFocusRef]);

  useEffect(()=>()=>{
    pageRestoreRef.current?.();pageRestoreRef.current=null;
    if(!wasOpenRef.current)return;
    const target=openerRef.current?.isConnected?openerRef.current:restoreFocusRef?.current;
    requestAnimationFrame(()=>target?.focus());
  },[restoreFocusRef]);

  async function confirm(){
    if(protectedBusy||confirmDisabled||confirmInFlightRef.current)return;
    confirmInFlightRef.current=true;
    setConfirming(true);
    try{await onConfirm()}
    finally{
      confirmInFlightRef.current=false;
      if(mountedRef.current)setConfirming(false);
    }
  }

  function cancel(){
    if(protectedBusy||confirmInFlightRef.current)return;
    onCancel();
  }

  const content=<dialog
    ref={dialogRef}
    className={`confirmation-dialog confirmation-dialog--${tone}`}
    aria-labelledby={titleId}
    aria-describedby={descriptionId}
    aria-modal={fullPage?'false':'true'}
    data-page={fullPage||undefined}
    aria-busy={protectedBusy||undefined}
    onCancel={event=>{event.preventDefault();cancel()}}
    onKeyDown={event=>{if(fullPage&&event.key==='Escape'){event.preventDefault();cancel();}}}
  >
    <div className="confirmation-dialog__surface">
      <header className="confirmation-dialog__header">
        <span className="confirmation-dialog__symbol" aria-hidden="true">!</span>
        <div>
          <p className="eyebrow">Please confirm</p>
          <h2 id={titleId} tabIndex={-1}>{title}</h2>
        </div>
      </header>
      <div className="confirmation-dialog__content" tabIndex={0} aria-label="Confirmation details">
        <div id={descriptionId} className="confirmation-dialog__description">{description}</div>
        {children}
      </div>
      <footer className="confirmation-dialog__actions">
        <button ref={cancelRef} type="button" className="secondary" disabled={protectedBusy} onClick={cancel}>{cancelLabel}</button>
        <button type="button" className="confirmation-dialog__confirm" disabled={protectedBusy||confirmDisabled} onClick={()=>void confirm()}>{protectedBusy?busyLabel:confirmLabel}</button>
      </footer>
    </div>
  </dialog>;
  return typeof document==='undefined'?content:createPortal(content,document.body);
}
